import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import httpRequest from '../utils/request';
import { toast } from 'react-toastify';
import {
  FaArrowLeft,
  FaUtensils,
  FaClock,
  FaCheck,
  FaCheckDouble,
  FaVolumeHigh,
  FaVolumeXmark,
  FaFire,
  FaUser
} from 'react-icons/fa6';

export default function KitchenDisplay() {
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Local browser state to track ready status of items/tables
  // Format: { [orderId]: { [itemProductId_variantId]: true } }
  const [readyItems, setReadyItems] = useState(() => {
    try {
      const saved = localStorage.getItem('kds_ready_items');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  // Track completed orders to hide them from KDS
  // Format: { [orderId]: true }
  const [readyOrders, setReadyOrders] = useState(() => {
    try {
      const saved = localStorage.getItem('kds_ready_orders');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  // Track known active order IDs to detect new ones and play sound
  const activeOrderIdsRef = useRef(new Set());

  // Real-time ticking state
  const [, setTick] = useState(0);

  // Persistence effect
  useEffect(() => {
    try {
      localStorage.setItem('kds_ready_items', JSON.stringify(readyItems));
    } catch (e) {}
  }, [readyItems]);

  useEffect(() => {
    try {
      localStorage.setItem('kds_ready_orders', JSON.stringify(readyOrders));
    } catch (e) {}
  }, [readyOrders]);

  // Synthesis Beep helper (uses Web Audio API to ensure reliable, zero-asset notifications)
  const playNotificationSound = () => {
    if (!soundEnabled) return;
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      
      // First beep (higher pitch)
      const osc1 = audioCtx.createOscillator();
      const gain1 = audioCtx.createGain();
      osc1.connect(gain1);
      gain1.connect(audioCtx.destination);
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(880, audioCtx.currentTime); // A5 note
      gain1.gain.setValueAtTime(0.1, audioCtx.currentTime);
      gain1.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.15);
      osc1.start();
      osc1.stop(audioCtx.currentTime + 0.15);

      // Second beep (slightly delayed, lower pitch)
      setTimeout(() => {
        try {
          const osc2 = audioCtx.createOscillator();
          const gain2 = audioCtx.createGain();
          osc2.connect(gain2);
          gain2.connect(audioCtx.destination);
          osc2.type = 'sine';
          osc2.frequency.setValueAtTime(1320, audioCtx.currentTime); // E6 note
          gain2.gain.setValueAtTime(0.1, audioCtx.currentTime);
          gain2.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.25);
          osc2.start();
          osc2.stop(audioCtx.currentTime + 0.25);
        } catch (e) {}
      }, 100);

    } catch (err) {
      console.warn('Web Audio API not supported or blocked:', err);
    }
  };

  // Poll active tables & orders
  const fetchActiveKDS = async (isFirstLoad = false) => {
    try {
      if (isFirstLoad) setLoading(true);
      const { data } = await httpRequest.get('/restaurant/tables');
      const tableList = Array.isArray(data) ? data : [];
      setTables(tableList);

      // Detect new active orders to ring sound alert
      const currentActiveIds = new Set();
      let hasNewOrder = false;

      let readyOrdersUpdated = false;
      const nextReadyOrders = { ...readyOrders };

      tableList.forEach(t => {
        if (t.activeOrderId && t.status !== 'free') {
          const orderId = t.activeOrderId._id || t.activeOrderId.id;
          currentActiveIds.add(orderId);

          const kitchenItems = (t.activeOrderId.items || []).filter(item => item.sentToKitchen && item.quantity > 0);
          const orderReadyScope = readyItems[orderId] || {};
          
          const hasUnreadyKitchenItems = kitchenItems.some(item => {
            const itemKey = `${item.productId}_${item.variantId || 'null'}`;
            const preparedQty = orderReadyScope[itemKey];
            const preparedCount = preparedQty === true ? 1 : (preparedQty ? Number(preparedQty) : 0);
            return preparedCount !== item.quantity;
          });

          if (hasUnreadyKitchenItems && readyOrders[orderId]) {
            delete nextReadyOrders[orderId];
            readyOrdersUpdated = true;
            hasNewOrder = true; 
          }
          
          // Check if this active order ID is brand new and not flagged as ready
          if (!activeOrderIdsRef.current.has(orderId) && !readyOrders[orderId]) {
            hasNewOrder = true;
          }
        }
      });

      if (readyOrdersUpdated) {
        setReadyOrders(nextReadyOrders);
      }

      // Update ref
      activeOrderIdsRef.current = currentActiveIds;

      // Play alert sound if new orders are received (excluding the very first load)
      if (hasNewOrder && !isFirstLoad) {
        playNotificationSound();
        toast.info('🔔 ¡Nueva comanda recibida en cocina!', { autoClose: 3000 });
      }

    } catch (err) {
      console.error('Error KDS polling:', err);
    } finally {
      if (isFirstLoad) setLoading(false);
    }
  };

  // Initial load and periodic polling effect (KDS is hands-free, so updates every 10s)
  useEffect(() => {
    fetchActiveKDS(true);

    const pollInterval = setInterval(() => {
      fetchActiveKDS(false);
    }, 10000);

    return () => clearInterval(pollInterval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyOrders]);

  // Clock ticking effect (updates elapsed minutes every 10 seconds)
  useEffect(() => {
    const clockTimer = setInterval(() => {
      setTick(prev => prev + 1);
    }, 10000);
    return () => clearInterval(clockTimer);
  }, []);

  // Recalculate timer minutes and return semaforo color info
  const getTimerInfo = (createdAtString) => {
    if (!createdAtString) return { minutes: 0, color: '#10b981', label: '0m' };
    const diffMs = Date.now() - new Date(createdAtString).getTime();
    const minutes = Math.floor(diffMs / 60000);

    let color = '#10b981'; // Green (< 15 mins)
    if (minutes >= 15 && minutes < 30) {
      color = '#f59e0b'; // Orange (15 - 30 mins)
    } else if (minutes >= 30) {
      color = '#ef4444'; // Red (> 30 mins, critical wait)
    }

    const label = minutes >= 60 ? 
      `${Math.floor(minutes / 60)}h ${minutes % 60}m` : 
      `${minutes}m`;

    return { minutes, color, label };
  };

  // Interaction handlers
  const handleItemToggleReady = (orderId, itemId, quantity) => {
    setReadyItems(prev => {
      const orderScope = prev[orderId] || {};
      const preparedQty = orderScope[itemId];
      const preparedCount = preparedQty === true ? 1 : (preparedQty ? Number(preparedQty) : 0);
      const isCurrentlyReady = preparedCount === quantity;
      const nextValue = isCurrentlyReady ? false : quantity;
      return {
        ...prev,
        [orderId]: {
          ...orderScope,
          [itemId]: nextValue
        }
      };
    });
  };

  const handleOrderComplete = async (orderId, tableNumber, tableId) => {
    // Mark locally as ready for UI feedback
    setReadyOrders(prev => ({
      ...prev,
      [orderId]: true
    }));
    toast.success(`🍳 Mesa #${tableNumber} marcada como terminada`);
    // Notify backend to set table readyToServe flag
    try {
      await httpRequest.post(`/restaurant/tables/${tableId}/ready`);
    } catch (err) {
      console.error('Error marking table ready on server:', err);
      toast.error('⚠️ No se pudo notificar la mesa como lista al servidor');
    }
  };

  const handleResetKDS = () => {
    if (window.confirm('¿Deseas vaciar el historial de comandas terminadas de esta pantalla?')) {
      setReadyOrders({});
      setReadyItems({});
      toast.success('🧹 Monitor de cocina restablecido');
    }
  };

  // Derive active comanda cards (exclude free tables and orders marked as ready/completed)
  const activeComandas = tables.filter(t => {
    if (t.status === 'free' || !t.activeOrderId) return false;
    const orderId = t.activeOrderId._id || t.activeOrderId.id;
    return !readyOrders[orderId];
  });

  // Critical counts
  const criticalCount = activeComandas.filter(t => {
    const timer = getTimerInfo(t.activeOrderId.createdAt);
    return timer.minutes >= 30;
  }).length;

  return (
    <div className="kds-container">
      {/* Premium KDS CSS Styles */}
      <style>{`
        .kds-container {
          padding: 20px;
          min-height: 100vh;
          font-family: 'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          background: #0f172a;
          color: #f8fafc;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-sizing: border-box;
        }

        .kds-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 20px;
          flex-shrink: 0;
          flex-wrap: wrap;
          gap: 12px;
          border-bottom: 1px solid #1e293b;
          padding-bottom: 16px;
        }

        .kds-title-wrapper {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .kds-title {
          font-size: 24px;
          font-weight: 800;
          margin: 0;
          letter-spacing: -0.5px;
          background: linear-gradient(135deg, #38bdf8 0%, #3b82f6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .kds-stats {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .kds-badge {
          padding: 6px 14px;
          border-radius: 8px;
          font-weight: 700;
          font-size: 13px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .kds-badge.active { background: #1e293b; color: #38bdf8; border: 1px solid #0284c7; }
        .kds-badge.critical { background: #fee2e2; color: #ef4444; border: 1px solid #fecaca; animation: blinker 1.5s linear infinite; }

        @keyframes blinker {
          50% { opacity: 0.6; }
        }

        .btn-kds-nav {
          padding: 8px 16px;
          border-radius: 10px;
          background: #1e293b;
          border: 1px solid #334155;
          color: #94a3b8;
          font-weight: 700;
          font-size: 13px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s;
        }

        .btn-kds-nav:hover {
          background: #334155;
          color: #f8fafc;
        }

        .kds-main-grid {
          flex: 1;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
          overflow-y: auto;
          padding-bottom: 24px;
        }

        /* Comanda columns */
        .kds-card {
          background: #1e293b;
          border-radius: 18px;
          border: 1px solid #334155;
          display: flex;
          flex-direction: column;
          max-height: 480px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.15);
          overflow: hidden;
          transition: transform 0.2s ease, border-color 0.2s ease;
        }

        .kds-card:hover {
          transform: translateY(-2px);
          border-color: #475569;
        }

        .kds-card-header {
          padding: 14px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #334155;
          flex-shrink: 0;
        }

        .kds-table-number {
          font-size: 20px;
          font-weight: 900;
          color: #f8fafc;
        }

        .kds-waiter {
          font-size: 12px;
          color: #94a3b8;
          font-weight: 600;
          margin-top: 2px;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .kds-timer-badge {
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 800;
          color: white;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .kds-items-content {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .kds-item-row {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 10px;
          border-radius: 10px;
          cursor: pointer;
          transition: background 0.15s;
          user-select: none;
          background: rgba(255,255,255,0.02);
        }

        .kds-item-row:hover {
          background: rgba(255,255,255,0.06);
        }

        .kds-qty {
          font-size: 18px;
          font-weight: 900;
          color: #38bdf8;
          min-width: 24px;
        }

        .kds-name-wrap {
          flex: 1;
        }

        .kds-item-name {
          font-size: 14px;
          font-weight: 700;
          color: #e2e8f0;
          transition: all 0.2s;
        }

        .kds-item-variant {
          font-size: 11px;
          color: #0284c7;
          font-weight: 700;
          margin-top: 2px;
        }

        .kds-item-notes {
          font-size: 11px;
          color: #f59e0b;
          border: 1px solid rgba(245, 158, 11, 0.3);
          background: rgba(245, 158, 11, 0.05);
          padding: 3px 6px;
          border-radius: 6px;
          margin-top: 6px;
          display: inline-block;
          font-weight: 700;
          line-height: 1.2;
        }

        /* Done Strikeout effect */
        .kds-item-row.done .kds-item-name {
          text-decoration: line-through;
          color: #64748b;
        }
        .kds-item-row.done .kds-qty {
          color: #475569;
        }
        .kds-item-row.done .kds-item-variant,
        .kds-item-row.done .kds-item-notes {
          opacity: 0.35;
        }

        .kds-card-footer {
          padding: 10px 16px;
          background: #1e293b;
          border-top: 1px solid #334155;
          flex-shrink: 0;
        }

        .btn-kds-complete {
          width: 100%;
          padding: 10px;
          border-radius: 8px;
          border: none;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          font-weight: 800;
          font-size: 13px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          box-shadow: 0 4px 6px rgba(16,185,129,0.15);
          transition: all 0.2s;
        }

        .btn-kds-complete:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 12px rgba(16,185,129,0.25);
        }

        .kds-empty {
          background: #1e293b;
          border-radius: 20px;
          padding: 60px 40px;
          text-align: center;
          border: 1px dashed #475569;
          color: #94a3b8;
          max-width: 600px;
          margin: 40px auto;
        }
      `}</style>

      {/* KDS Monitor Header Toolbar */}
      <header className="kds-header">
        <div className="kds-title-wrapper">
          <FaUtensils style={{ fontSize: 24, color: '#38bdf8' }} />
          <h1 className="kds-title">Monitor de Cocina KDS</h1>
        </div>

        <div className="kds-stats">
          <div className="kds-badge active">
            <span>Comandas: {activeComandas.length}</span>
          </div>
          {criticalCount > 0 && (
            <div className="kds-badge critical">
              <FaFire /> Demora Crítica: {criticalCount}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button 
            onClick={() => setSoundEnabled(!soundEnabled)} 
            className="btn-kds-nav"
            title={soundEnabled ? 'Silenciar alertas' : 'Habilitar alertas'}
          >
            {soundEnabled ? <FaVolumeHigh /> : <FaVolumeXmark />}
            {soundEnabled ? 'Sonido ON' : 'Sonido OFF'}
          </button>
          
          <button onClick={handleResetKDS} className="btn-kds-nav" title="Limpiar historial de listos">
            Restablecer KDS
          </button>
          
          <button onClick={() => navigate('/dashboard')} className="btn-kds-nav">
            <FaArrowLeft /> Panel
          </button>
        </div>
      </header>

      {/* Main KDS Grid container */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '100px 0', fontSize: '18px', color: '#94a3b8' }}>
          🔄 Cargando pedidos de cocina...
        </div>
      ) : activeComandas.length === 0 ? (
        <div className="kds-empty">
          <span style={{ fontSize: 60, display: 'block', marginBottom: 12 }}>🍳</span>
          <h2>Sin comandas pendientes</h2>
          <p style={{ marginTop: 8 }}>Cuando los mozos carguen productos y presionen "Comanda" en el POS, aparecerán inmediatamente aquí.</p>
        </div>
      ) : (
        <div className="kds-main-grid">
          {activeComandas.map((t) => {
            const o = t.activeOrderId;
            const orderId = o._id || o.id;
            
            // Only show items that have been sent to kitchen
            const kitchenItems = (o.items || []).filter(item => item.sentToKitchen && item.quantity > 0);
            
            // Recalculate elapsed time
            const timer = getTimerInfo(o.createdAt);
            
            // Check if all kitchen items of this order are flagged ready locally
            const orderReadyScope = readyItems[orderId] || {};
            const allItemsReady = kitchenItems.length > 0 && kitchenItems.every(item => {
              const key = `${item.productId}_${item.variantId || 'null'}`;
              const preparedQty = orderReadyScope[key];
              const preparedCount = preparedQty === true ? 1 : (preparedQty ? Number(preparedQty) : 0);
              return preparedCount === item.quantity;
            });

            // If no items are sent to kitchen, we don't display a card in KDS
            if (kitchenItems.length === 0) return null;

            return (
              <div 
                key={t._id} 
                className="kds-card"
                style={{ opacity: allItemsReady ? 0.65 : 1 }}
              >
                {/* Card Header details */}
                <div className="kds-card-header">
                  <div>
                    <span className="kds-table-number">{t.name || `Mesa #${t.number}`}</span>
                    <div className="kds-waiter">
                      <FaUser style={{ fontSize: 10 }} /> Mozo: {o.waiterId?.name || 'Mozo'}
                    </div>
                  </div>
                  <span 
                    className="kds-timer-badge"
                    style={{ backgroundColor: timer.color }}
                  >
                    <FaClock style={{ fontSize: 11 }} /> {timer.label}
                  </span>
                </div>

                {/* Card Body - List of items */}
                <div className="kds-items-content">
                  {kitchenItems.map((item, index) => {
                    const itemKey = `${item.productId}_${item.variantId || 'null'}`;
                    const preparedQty = orderReadyScope[itemKey];
                    const preparedCount = preparedQty === true ? 1 : (preparedQty ? Number(preparedQty) : 0);
                    const isDone = preparedCount === item.quantity;

                    return (
                      <div 
                        key={index} 
                        onClick={() => handleItemToggleReady(orderId, itemKey, item.quantity)}
                        className={`kds-item-row ${isDone ? 'done' : ''}`}
                      >
                        <span className="kds-qty">{item.quantity}</span>
                        <div className="kds-name-wrap">
                          <div className="kds-item-name">{item.name}</div>
                          {item.variantName && (
                            <div className="kds-item-variant">{item.variantName}</div>
                          )}
                          {item.notes && (
                            <div className="kds-item-notes">{item.notes}</div>
                          )}
                        </div>
                        {isDone && (
                          <FaCheck style={{ color: '#10b981', alignSelf: 'center', flexShrink: 0 }} />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Card Footer actions */}
                <div className="kds-card-footer">
                  <button 
                    onClick={() => handleOrderComplete(orderId, t.number, t._id)}
                    className="btn-kds-complete"
                    style={{ background: allItemsReady ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : '#475569' }}
                  >
                    <FaCheckDouble /> Listo Mesa
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
